import { useState } from 'react';

// components
import Image from 'next/image';
import { Icon } from '@/components/ui/icon';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';

const colors = {
  brand: {
    title: 'text-brand-300',
    card: 'bg-brand-400',
    shadow: 'bg-brand-500'
  },
  purple: {
    title: 'text-purple-300',
    card: 'bg-purple-400',
    shadow: 'bg-purple-500'
  }
};

const FeaturesHighlightGallery = ({ subtitle, title, galleryVariant: variant, galleryColor: color, features }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const colorClasses = colors[color] || colors.brand;

  const activeFeature = features[activeIndex] || features[0];

  return (
    <div className="mx-auto w-[90vw] py-16 xl:w-(--container-width) xl:py-32">
      <VisibleLoading delay={75}>
        {({ ref, className }) => (
          <div
            ref={ref}
            className={`txt-md mx-auto max-w-[800px] text-center font-semibold uppercase ${colorClasses.title} ${className || ''}`.trim()}
          >
            {subtitle}
          </div>
        )}
      </VisibleLoading>

      <VisibleLoading delay={150}>
        {({ ref, className }) => (
          <h4 ref={ref} className={`mx-auto my-12 max-w-[800px] text-center ${className || ''}`.trim()}>
            {title}
          </h4>
        )}
      </VisibleLoading>

      <div
        className={`flex flex-wrap gap-6 ${variant === 'right' ? 'flex-col-reverse xl:flex-row-reverse' : 'flex-col-reverse xl:flex-row'} `}
      >
        <div className="relative z-2 w-full space-y-2 xl:w-[390px]">
          {features.map((feature, index) => (
            <VisibleLoading key={feature.id} delay={(index + 1) * 100}>
              {({ ref, className }) => (
                <FeaturesSmallCard
                  innerRef={ref}
                  className={className}
                  index={index}
                  feature={feature}
                  isHighlighted={activeIndex === index}
                  colorClasses={colorClasses}
                  onSelect={setActiveIndex}
                />
              )}
            </VisibleLoading>
          ))}
        </div>
        <div className="relative z-1 flex-1 xl:max-h-[720px]">
          <VisibleLoading delay={500} opacity={40}>
            {({ ref, className }) => (
              <div
                ref={ref}
                className={`${colorClasses.shadow} absolute hidden ${variant === 'right' ? '-left-[25%]' : '-right-[25%]'} -top-[5%] z-1 h-[405px] w-[405px] transform-gpu rounded-full blur-[150px] ${className || ''}`.trim()}
              />
            )}
          </VisibleLoading>
          <VisibleLoading delay={500} opacity={30}>
            {({ ref, className }) => (
              <div
                ref={ref}
                className={`${colorClasses.shadow} absolute ${variant === 'right' ? '-right-[15%]' : '-left-[15%]'} -bottom-[5%] z-1 h-[405px] w-[405px] transform-gpu rounded-full blur-[150px] ${className || ''}`.trim()}
              />
            )}
          </VisibleLoading>

          <VisibleLoading delay={300} duration={1000}>
            {({ ref, className }) => (
              <div
                ref={ref}
                className={`relative z-2 h-[400px] w-full rounded-3xl bg-[rgba(44,_39,_37,_0.30)] p-10 xl:h-full xl:p-20 ${className || ''}`.trim()}
              >
                {activeFeature?.image && (
                  <Image
                    src={getImagePath(activeFeature.image)}
                    width={activeFeature.image.width}
                    height={activeFeature.image.height}
                    alt={activeFeature.image.alternativeText}
                    className="h-full w-full object-contain"
                  />
                )}
              </div>
            )}
          </VisibleLoading>
        </div>
      </div>
    </div>
  );
};
export default FeaturesHighlightGallery;

export const FeaturesSmallCard = ({ index, innerRef, className, feature, isHighlighted, colorClasses, onSelect }) => {
  const backgroundColorClass = isHighlighted ? colorClasses.card : 'bg-[#2C2725]';

  return (
    <div
      ref={innerRef}
      onMouseEnter={() => onSelect(index)}
      onClick={() => onSelect(index)}
      className={`group relative w-full px-2 ${className || ''}`.trim()}
    >
      <div className={`flex items-center gap-3 rounded-xl transition-colors ${backgroundColorClass} px-4 py-3`}>
        <div
          className={`flex h-[40px] w-[40px] items-center justify-center rounded-full transition-colors ${isHighlighted ? 'bg-white' : 'bg-linear-[45deg,transparent_10%,var(--color-white-50)]'}`}
        >
          <div
            className={`flex h-[38px] w-[38px] items-center justify-center rounded-full transition-colors ${backgroundColorClass}`}
          >
            <Icon type={feature.icon} width={20} height={20} />
          </div>
        </div>
        <div className="txt-md flex-1">{feature.text}</div>
      </div>
    </div>
  );
};
